import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Shield, ShieldOff, RotateCcw, Globe, Clock, AlertTriangle } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RateLimitManagement() {
  var { token } = useAuth();
  var [records, setRecords] = useState([]);
  var [loading, setLoading] = useState(true);
  var [actionLoading, setActionLoading] = useState('');

  var fetchRecords = function () {
    setLoading(true);
    axios.get(API_URL + '/api/admin/rate-limits?authorization=' + token)
      .then(function (r) { setRecords(r.data.rate_limits || []); })
      .catch(function () { toast.error('Failed to load rate limits'); })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchRecords(); }, [token]);

  var handleReset = function (ip) {
    setActionLoading('reset-' + ip);
    axios.post(API_URL + '/api/admin/rate-limits/' + encodeURIComponent(ip) + '/reset?authorization=' + token)
      .then(function () { toast.success('Count reset for ' + ip); fetchRecords(); })
      .catch(function () { toast.error('Failed to reset'); })
      .finally(function () { setActionLoading(''); });
  };

  var handleBlock = function (ip) {
    setActionLoading('block-' + ip);
    axios.post(API_URL + '/api/admin/rate-limits/' + encodeURIComponent(ip) + '/block?authorization=' + token)
      .then(function () { toast.success('IP ' + ip + ' blocked'); fetchRecords(); })
      .catch(function () { toast.error('Failed to block'); })
      .finally(function () { setActionLoading(''); });
  };

  var handleUnblock = function (ip) {
    setActionLoading('unblock-' + ip);
    axios.post(API_URL + '/api/admin/rate-limits/' + encodeURIComponent(ip) + '/unblock?authorization=' + token)
      .then(function () { toast.success('IP ' + ip + ' unblocked'); fetchRecords(); })
      .catch(function () { toast.error('Failed to unblock'); })
      .finally(function () { setActionLoading(''); });
  };

  var formatDate = function (d) {
    return d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  };

  var totalLimited = records.filter(function (r) { return r.usage_count >= 5; }).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="rate-limit-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rate Limit Management</h1>
          <p className="text-slate-600">Monitor and manage IP-based rate limits for the public comparison tool</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total IPs Tracked</p>
                <p className="text-xl font-bold text-slate-900" data-testid="total-ips">{records.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Rate Limited</p>
                <p className="text-xl font-bold text-slate-900" data-testid="total-limited">{totalLimited}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : records.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Globe className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No rate limit records yet. Records will appear when the public portal is used.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rate-limits-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 font-medium text-slate-600">IP Address</th>
                      <th className="text-left p-3 font-medium text-slate-600">Usage (/ 5)</th>
                      <th className="text-left p-3 font-medium text-slate-600">Status</th>
                      <th className="text-left p-3 font-medium text-slate-600">Window Start</th>
                      <th className="text-left p-3 font-medium text-slate-600">Last Request</th>
                      <th className="text-right p-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(function (rec) {
                      var isLimited = rec.usage_count >= 5;
                      return (
                        <tr key={rec.ip_address} className="border-b border-slate-100 hover:bg-slate-50" data-testid={'rate-limit-row-' + rec.ip_address}>
                          <td className="p-3 font-mono text-slate-900">{rec.ip_address}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
                                <div
                                  className={'h-full rounded-full ' + (rec.usage_count >= 5 ? 'bg-red-500' : rec.usage_count >= 3 ? 'bg-amber-500' : 'bg-green-500')}
                                  style={{ width: Math.min(100, (rec.usage_count / 5) * 100) + '%' }}
                                />
                              </div>
                              <span className="text-slate-700 font-medium">{rec.usage_count}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            {isLimited ? (
                              <Badge className="bg-amber-100 text-amber-700">Rate Limited</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Active</Badge>
                            )}
                          </td>
                          <td className="p-3 text-slate-600">{formatDate(rec.window_start)}</td>
                          <td className="p-3 text-slate-600">{formatDate(rec.last_request_at)}</td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={function () { handleReset(rec.ip_address); }}
                                disabled={actionLoading === 'reset-' + rec.ip_address}
                                title="Reset count"
                                data-testid={'reset-btn-' + rec.ip_address}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
